/* eslint-disable */
import React from 'react';
import { Route } from 'react-router';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@zephyr-abl-tech/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
  ImportStepper,
} from '@backstage/plugin-catalog-import';
import {
  ScaffolderPage,
  scaffolderPlugin,
} from '@zephyr-abl-tech/plugin-scaffolder';
import { orgPlugin } from '@zephyr-abl-tech/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  DefaultTechDocsHome,
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import {
  AlertDisplay,
  Content,
  ContentHeader,
  Header,
  OAuthRequestDialog,
  Page,
  SignInPage,
  SignInProviderConfig,
  SupportButton,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { PermissionedRoute } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { DevopsProjectsPage } from '@zephyr-abl-tech/plugin-devops-projects';
import { microsoftAuthApiRef, useApi } from '@backstage/core-plugin-api';
// import { CostInsightsPage } from '@zephyr-abl-tech/plugin-cost-insights';
import { ArgocdNotificationsPluginPage } from '@zephyr-abl-tech/argocd-notification-plugin';
import {
  createTheme,
  darkTheme,
  genPageTheme,
  BackstageTheme,
  lightTheme,
  shapes,
} from '@backstage/theme';
import { ThemeProvider } from '@material-ui/core/styles';
import LightIcon from '@material-ui/icons/WbSunny';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import CssBaseline from '@material-ui/core/CssBaseline';
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { HomePage } from './components/home/HomePage';
import { ScaffolderFieldExtensions } from '@zephyr-abl-tech/plugin-scaffolder';
/**
 * The `@backstage/core-components` package exposes this type that
 * contains all Backstage and `material-ui` components that can be
 * overridden along with the classes key those components use.
 */
import { BackstageOverrides } from '@backstage/core-components';
import { TechWrapperRadar } from './lib/techRadar/techRadarWrapper';
import { FeatureFlagged } from '@backstage/core-app-api';
import {
  AddAdditionalSecurityExemptionExtension,
  BusinessUnitDropdownExtension,
  CustomDatePickerExtension,
  CustomExistingOnly,
  CustomPRExtension,
  DepartmentDropdownExtension,
  ExistingRepoDropdownExtension,
  NewRepoNameOnlyExtension,
  SecurityExceptionForm,
  SquadDropdownExtension,
  TechdocsFolderExtension,
} from './scaffolder/CustomExtension/extensions';
import UserContextProvider from './context/userContext';
import { LightBox } from '@backstage/plugin-techdocs-module-addons-contrib';

/* eslint-enable */

import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import {
  AppInsightsContext,
  withAITracking,
} from '@microsoft/applicationinsights-react-js';
import { ApplicationInsight, reactPlugin } from './Utils/ApplicationInsight';
import { SettingsPageTest } from './components/settings/SettingsPage';
import BannerContextProvider from './components/home/bannerComponent/bannerContext';
import { FeedbackForm } from './components/feedbackform/Feedbackform';
import { StructurizrApp } from './components/structurizr/structurizr';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { TechdocssupportPage } from '@backstage/plugin-techdocssupport';
import { VisitListener } from '@backstage/plugin-home';
import { Reader } from './components/techdocs/techdocsReader';
import { CustomTechDocsHome } from './components/techdocs/techdocsHomePage';
import ChatbotPage from './components/chatbot/chatbotPage';
import { CustomCatalog } from './components/catalog/CustomCatalog';
import { CustomAPI } from './components/catalog/CustomAPIPage';
import { Box, Grid } from '@material-ui/core';

const azureProvider: SignInProviderConfig = {
  id: 'azure-auth-provider',
  title: 'AcuityBrands SSO',
  message: 'Sign in using Azure SSO',
  apiRef: microsoftAuthApiRef,
};

// You can add overrides here. Both dark and light themes pull this in. Changes here will apply to both.
export const createCustomThemeOverrides = (
  theme: BackstageTheme,
): BackstageOverrides => {
  return {
    BackstageSidebarItem: {
      label: {
        whiteSpace: 'pre-wrap',
        overflowWrap: 'break-word',
      },
    },
  };
};

const LighthemeOptions = {
  palette: {
    ...lightTheme.palette,
    primary: {
      main: '#0076A7',
    },
    secondary: {
      main: '#15eb2e',
      default: '#333333',
    },
    background: {
      default: '#F8F8F8',
      paper: '#FFFFFF',
    },
    navigation: {
      background: '#1A1919',
      indicator: '#FFFFFF',
      color: '#ACACAC',
      selectedColor: '#FFFFFF',
    },
  },
  defaultPageTheme: 'home',
  fontFamily: 'Roboto',
  /* below drives the header colors */
  pageTheme: {
    home: genPageTheme({ colors: ['#1A1919'], shape: shapes.wave }),
    documentation: genPageTheme({ colors: ['#1A1919'], shape: shapes.wave2 }),
    tool: genPageTheme({ colors: ['#1A1919'], shape: shapes.round }),
    service: genPageTheme({ colors: ['#1A1919'], shape: shapes.wave }),
    website: genPageTheme({ colors: ['#1A1919'], shape: shapes.wave }),
    library: genPageTheme({ colors: ['#1A1919'], shape: shapes.wave }),
    other: genPageTheme({ colors: ['#1A1919'], shape: shapes.wave }),
    app: genPageTheme({ colors: ['#1A1919'], shape: shapes.wave }),
    apis: genPageTheme({ colors: ['#1A1919'], shape: shapes.wave }),
    homePageUser0: genPageTheme({ colors: ['#008000'], shape: shapes.wave }),
    homePageUser2: genPageTheme({ colors: ['#AA4586'], shape: shapes.wave }),
    homePageUser1: genPageTheme({ colors: ['#6a0dad'], shape: shapes.wave2 }),
    homePageUser3: genPageTheme({ colors: ['#15acbc'], shape: shapes.wave2 }),
  },
};

const bsLightTheme = createTheme({
  palette: LighthemeOptions.palette,
  fontFamily: LighthemeOptions.fontFamily,
  defaultPageTheme: 'home',
  pageTheme: LighthemeOptions.pageTheme,
});
const customLightTheme: BackstageTheme = {
  ...bsLightTheme,
  overrides: {
    // These are the overrides that Backstage applies to `material-ui` components
    ...lightTheme.overrides,

    // These are your custom overrides, either to `material-ui` or Backstage components.
    ...createCustomThemeOverrides(lightTheme),

    MuiButton: {
      label: {
        color: '#FFFFFF',
      },
      root: {
        backgroundColor: '#0076A7',
        border: '1px #0076A7 solid',
        variant: 'outlined',
        borderRadius: '50px',
        paddingLeft: '16px',
        paddingRight: '16px',
        minWidth: '50px',
        '&:focus': {
          color: '#0076A7',
          backgroundColor: '#0076A7',
          variant: 'outlined',
          border: '1px #00305B solid',
        },
        '&:hover': {
          border: '1px #004D7A solid',
          opacity: 1,
          variant: 'outlined',
          backgroundColor: '#004D7A !important',
          color: '#004D7A',
        },
        '&:active': {
          backgroundColor: '#00305B',
        },
        '&:disabled': {
          border: '1px #9E9E9E solid',
          backgroundColor: '#9E9E9E',
        },
      },
    },
    MuiTable: {
      root: {
        padding: '16px 16px 16px 16px',
      },
    },
  },
};

const darkThemeOptions = {
  palette: {
    ...darkTheme.palette,
    primary: {
      main: '#64D9DB',
    },
    secondary: {
      main: '#333333',
      default: '#FFFFFF',
    },
    background: {
      default: '#333333',
      paper: '#424242',
    },
    navigation: {
      background: '#202020',
      indicator: '#64D9DB',
      color: '#ACACAC',
      selectedColor: '#64D9DB',
    },
  },
  defaultPageTheme: 'home',
  fontFamily: 'Roboto',
  /* below drives the header colors */
  pageTheme: {
    home: genPageTheme({ colors: ['#1A1919'], shape: shapes.wave }),
    documentation: genPageTheme({ colors: ['#1A1919'], shape: shapes.wave2 }),
    tool: genPageTheme({ colors: ['#1A1919'], shape: shapes.round }),
    service: genPageTheme({ colors: ['#1A1919'], shape: shapes.wave }),
    website: genPageTheme({ colors: ['#1A1919'], shape: shapes.wave }),
    library: genPageTheme({ colors: ['#1A1919'], shape: shapes.wave }),
    other: genPageTheme({ colors: ['#1A1919'], shape: shapes.wave }),
    app: genPageTheme({ colors: ['#1A1919'], shape: shapes.wave }),
    apis: genPageTheme({ colors: ['#1A1919'], shape: shapes.wave }),
    homePageUser1: genPageTheme({ colors: ['#008000'], shape: shapes.wave2 }),
    homePageUser2: genPageTheme({ colors: ['#dd18dc'], shape: shapes.wave }),
    homePageUser0: genPageTheme({ colors: ['#6a0dad'], shape: shapes.wave }),
    homePageUser3: genPageTheme({ colors: ['#118a96'], shape: shapes.wave2 }),
  },
};

const bsDarkTheme = createTheme({
  palette: darkThemeOptions.palette,
  fontFamily: darkThemeOptions.fontFamily,
  defaultPageTheme: 'home',
  pageTheme: darkThemeOptions.pageTheme,
});

const customDarkTheme: BackstageTheme = {
  ...bsDarkTheme,
  overrides: {
    // These are the overrides that Backstage applies to `material-ui` components
    ...darkTheme.overrides,

    // These are your custom overrides, either to `material-ui` or Backstage components.
    ...createCustomThemeOverrides(darkTheme),

    MuiButton: {
      label: {
        color: '#2F2F2F',
      },
      root: {
        backgroundColor: '#64D9DB',
        color: 'red',
        border: '2px #64D9DB solid',
        variant: 'outlined',
        borderRadius: '50px',
        paddingLeft: '16px',
        paddingRight: '16px',
        minWidth: '50px',
        '&:focus': {
          color: '#64D9DB',
          backgroundColor: '#64D9DB',
          variant: 'outlined',
          border: '2px #D4FBFB solid',
        },
        '&:hover': {
          border: '2px #A9F6F8 solid',
          opacity: 1,
          variant: 'outlined',
          backgroundColor: '#A9F6F8 !important',
          color: '#A9F6F8',
        },
        // "&:active": {
        //   backgroundColor: '#D4FBFB',
        // },
        '&:disabled': {
          border: '2px #616161 solid',
          backgroundColor: '#616161',
        },
      },
    },
    MuiLink: {
      root: {
        color: '#64D9DB',
        '&:hover': {
          color: '#A9F6F8',
        },
      },
    },
  },
};

const app = createApp({
  apis,
  featureFlags: [
    {
      pluginId: 'show-struct', // pluginId is required for feature flags in plugins. It can be left blank for a feature flag leveraged in the application.
      name: 'show-struct',
      description: 'Structurizr WSL translator',
    },
  ],
  themes: [
    {
      id: 'light-theme',
      title: 'Light',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <ThemeProvider theme={customLightTheme}>
          <CssBaseline>{children}</CssBaseline>
        </ThemeProvider>
      ),
    },
    {
      id: 'dark-theme',
      title: 'Dark',
      variant: 'dark',
      icon: <DarkModeIcon />,
      Provider: ({ children }) => (
        <ThemeProvider theme={customDarkTheme}>
          <CssBaseline>{children}</CssBaseline>
        </ThemeProvider>
      ),
    },
  ],
  components: {
    SignInPage: props => (
      <SignInPage {...props} auto provider={azureProvider} />
    ),
  },
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
});

const AppProvider = app.getProvider();
const AppRouter = app.getRouter();

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    <Route path="/catalog" element={<CatalogIndexPage />}>
      <CustomCatalog />
    </Route>{' '}
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />}>
      <CustomTechDocsHome />
    </Route>
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <Reader />
      <TechDocsAddons>
        <LightBox />
        <TechdocssupportPage />
      </TechDocsAddons>
    </Route>
    ;
    <Route path="/create" element={<ScaffolderPage />}>
      <ScaffolderFieldExtensions>
        <CustomPRExtension />
        <CustomExistingOnly />
        <SquadDropdownExtension />
        <TechdocsFolderExtension />
        <NewRepoNameOnlyExtension />
        <ExistingRepoDropdownExtension />
        <BusinessUnitDropdownExtension />
        <DepartmentDropdownExtension />
        <CustomDatePickerExtension />
        <SecurityExceptionForm />
        <AddAdditionalSecurityExemptionExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />}>
      <CustomAPI />
    </Route>
    <Route path="/tech-radar/:name" element={<TechWrapperRadar />} />
    <Route path="/catalog-import" element={<CatalogImportPage />}>
      <Page themeId="home">
        <Header title="Register an existing component" />
        <Content>
          <ContentHeader title="Start tracking your components">
            <SupportButton>
              Start tracking your component in Backstage by adding it to the
              software catalog.
            </SupportButton>
          </ContentHeader>

          <Grid container direction="row">
            <Grid item xs={12}>
              <Box sx={{ width: '100%' }}>
                <ImportStepper />
              </Box>
            </Grid>
          </Grid>
        </Content>
      </Page>
    </Route>
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<SettingsPageTest />} />
    <FeatureFlagged with="show-argocd-notifications">
      <Route
        path="/argocd-notifications"
        element={<ArgocdNotificationsPluginPage />}
      />
    </FeatureFlagged>
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/devops-projects" element={<DevopsProjectsPage />} />
    <Route path="/portal-feedback-form" element={<FeedbackForm />} />
    <Route path="/structurizr-app" element={<StructurizrApp />} />
    <FeatureFlagged with="show-chatbot">
      <Route path="/chatbot" element={<ChatbotPage />} />
    </FeatureFlagged>
    <FeatureFlagged with="show-cost-insights">
      {/* <Route path="/cost-insights" element={<CostInsightsPage />} /> */}
    </FeatureFlagged>
    <Route path="/techdocssupport" element={<TechdocssupportPage />} />
  </FlatRoutes>
);

const App = () => (
  <AppProvider>
    <ApplicationInsight>
      <AppInsightsContext.Provider value={reactPlugin}>
        <UserContextProvider>
          <BannerContextProvider>
            <AlertDisplay />
            <OAuthRequestDialog />
            <AppRouter>
              <VisitListener />
              <Root>{routes}</Root>
            </AppRouter>
          </BannerContextProvider>
        </UserContextProvider>
      </AppInsightsContext.Provider>
    </ApplicationInsight>
  </AppProvider>
);

export default withAITracking(reactPlugin, App);
